import { createSelector } from '@ngrx/store';
import { CreateTellentApp, SeatKind, TellentApps } from '@tellent/types';

import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  memoizeSelector,
  ngrxEvent,
} from '@recruitee/ngrx';

import { EntityStoreKey } from '../types';

const idSelector = ({ guid }: { guid: string }) => guid;

const APPS_ORDER = [SeatKind.Recruitment, SeatKind.CoreHR];

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<TellentApps>()(
  EntityStoreKey.TellentApps,
  {
    idSelector,
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);
      return {
        createApp: ngrxEvent<CreateTellentApp>(),
        createAppSuccess: (state, payload: { data: TellentApps }) =>
          adapter.addOne(state, payload.data),
        createAppFailed: (state, payload: { error: string }) => state,
        fetchCollectionSuccess: (state, payload: { data: TellentApps[] }) => {
          const payloadWithSortedApps = {
            ...payload,
            data: [...payload.data].sort(
              (a, b) => APPS_ORDER.indexOf(a.kind) - APPS_ORDER.indexOf(b.kind),
            ),
          };

          return defaultActions.fetchCollectionSuccess(state, payloadWithSortedApps);
        },
      };
    },
  },
);

const appsTypeSelector = createSelector(selectors.entities(), (data): SeatKind[] =>
  data.map(({ kind }) => kind),
);

const hasNoActiveSubscriptions = createSelector(selectors.entities(), (apps): boolean =>
  apps.every(app => !app.billing || ['trialing', 'inactive'].includes(app.billing.status)),
);

export const selectRolesSelectOptions = memoizeSelector((seatKind: SeatKind) =>
  createSelector(
    selectAppBySeatKind(seatKind),
    app =>
      app?.roles.map(({ externalId, name }) => ({
        label: name,
        value: externalId,
      })) || [],
  ),
);

export const selectDefaultRoleBySeatKind = memoizeSelector((seatKind: SeatKind) =>
  createSelector(
    selectAppBySeatKind(seatKind),
    app => app?.roles.find(role => role.default) || app?.roles[0],
  ),
);

export const selectAppBySeatKind = memoizeSelector((seatKind: SeatKind) => {
  const selector = selectors.entities();
  return createSelector(selector, apps => apps.find(app => app.kind === seatKind));
});

const TellentAppsSelectors = {
  ...selectors,
  selectRolesSelectOptions,
  selectAppBySeatKind,
  selectDefaultRoleBySeatKind,
  appsTypeSelector,
  hasNoActiveSubscriptions,
};

export {
  actions as TellentAppsActions,
  TellentAppsSelectors,
  reducer as TellentAppsReducer,
  namespace as TellentAppsNamespace,
};
